import { isValidBasePrice } from '@acme/b2b/pricing';
import { ApiService } from '@b2b/common/services/api/types';
import { BugsnagService, BugsnagServiceModule } from '@b2b/common/services/bugsnag';
import { FeatureService, FeatureServiceModule } from '@b2b/common/services/feature/feature.service';
import { PermissionsService } from '@b2b/common/services/permissions';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { ValidateService } from '@b2b/common/services/validate';
import { ensureArray } from '@b2b/common/utils/coerce-array';
import { TODO, UIBModalService, WrappedACMEErrorResponse } from '@b2b/types/core';
import { StateParams, StateService } from '@uirouter/angularjs';
import angular, { IFilterService, IQService, IRootScopeService, ISCEService, IScope, ITimeoutService } from 'angular';
import { TemplatesCreateExceptionsControllerModule } from '../exceptions/controllers/TemplatesCreateExceptionsController';
import {
  PublishingInProgressModalResult,
  TemplatesModalsPublishingInProgressControllerModule,
} from '../../modals/publishingInProgress/controllers/TemplatesModalsPublishingInProgressController';
import { PublishingStatus, PUBLISHING_STATUS_POLL_INTERVAL_MS } from '../../constants/publishing-status.constants';
import { Actor, createActor } from 'xstate';
import { createPollingMachine } from '@b2b/common/machines/polling.machine';

TemplatesCreateController.$inject = [
  '$scope',
  '$rootScope',
  '$state',
  '$timeout',
  '$uibModal',
  '$filter',
  '$controller',
  '$q',
  '$sce',
  'BugsnagService',
  'ApiService',
  'TemplatesService',
  'CalendarService',
  'HelpersService',
  'PermissionsService',
  'ValidateService',
  'WizardFactory',
  'UserPreferencesService',
  'FeatureService',
  'ThemesHelperService',
];

function TemplatesCreateController(
  $scope: IScope & { templatesCreate: TODO },
  $rootScope: IRootScopeService,
  $state: StateService,
  $timeout: ITimeoutService,
  $uibModal: UIBModalService,
  $filter: IFilterService,
  $controller: TODO,
  $q: IQService,
  $sce: ISCEService,
  bugsnagService: BugsnagService,
  ApiService: ApiService,
  TemplatesService: TODO,
  CalendarService: TODO,
  HelpersService: TODO,
  PermissionsService: PermissionsService,
  ValidateService: ValidateService,
  WizardFactory: TODO,
  UserPreferencesService: TODO,
  FeatureService: FeatureService,
  ThemesHelperService: ThemesHelperService,
) {
  const ctrl = {
    model: {} as TODO,
    entitlementConfig: [] as TODO[],
    salesRuleConfig: [] as TODO[],
    ticketRuleConfig: [] as TODO[],
    couponDiscountsConfig: [] as TODO[],
    accountDiscountsConfig: [] as TODO[],

    wizard: {} as TODO,
    tabErrors: {},

    emailSetting: 'always',
    company: ThemesHelperService.getCompany(),

    mode: 'create',
    validators: [] as TODO[],
    lastUpdated: false as string | false,
    lastPublished: false as string | false,
    isUpdate: false,
    isSaving: false,
    isPublishing: false,
    isSubmitted: false,
    isLoaded: false,
    hasDeletedEvents: false,
    colorCodes: {
      textColor: '',
      backgroundColor: '',
    },
    spectrumConfig: {
      titleText: 'Backoffice calendar color',
    } as { titleText: string; color?: string },
    cap: HelpersService.cap,
    dateFormat: UserPreferencesService.getDateFormat('standardDate', 'dateFormat'),
    hourFormat: UserPreferencesService.getDateFormat('standardTime', 'hourFormat'),

    templatesService: TemplatesService,
    publishingStatusPollingActor: null as Actor<
      ReturnType<typeof createPollingMachine<{ publishingStatus: string }>>
    > | null,

    init: function () {
      ctrl.templatesService.setIsLoaded(ctrl.isLoaded);

      $scope.$on('TemplatesCreateController::getDeletedEvents', ctrl.onDeletedEventsUpdate);
      $scope.$on('TemplatesCreateController::Error', ctrl.onError);
      $scope.$on('TemplatesCreateController::TabCheck', ctrl.onTabCheck);
      $scope.$on('UserPreferencesService:onDateFormatLoad', ctrl.onDateFormatLoad);

      $scope.$watchCollection('templatesCreate.colorCodes', ctrl.onColorCodesUpdate);

      // Clean up polling actor and overlay status when controller is destroyed
      $scope.$on('$destroy', () => {
        ctrl.stopPublishingStatusPolling();
        $rootScope.$broadcast('Application::setOverlayStatus', null, { wide: false });
      });

      TemplatesService.resetModel();

      ctrl.initWizard();

      ctrl.clearTabErrors();

      ValidateService.errors = [];

      ValidateService.addFieldTests(ctrl.wizard.getStep('create').validators);

      if ($state.params && $state.params.id) {
        ctrl.mode = 'edit';
        ctrl.getTemplate($state.params.id);
      } else {
        ValidateService.clearFieldTests();

        ctrl.model = TemplatesService.model;

        ctrl.setUpdateTimes(ctrl.model);

        if (CalendarService.current.hasOwnProperty('startTime')) {
          ctrl.model.name = CalendarService.current.name;

          const schedule = TemplatesService.getNewSchedule().schedule;
          if (schedule) {
            schedule.effectiveStartTime = CalendarService.current.startTime;
            schedule.effectiveEndTime = CalendarService.current.endTime;

            TemplatesService.setSchedule(schedule);
          }
        }

        TemplatesService.setModel(ctrl.model);
        ctrl.model = TemplatesService.model;
        ctrl.setTitleBar();
        ctrl.initColorPicker();

        ctrl.isLoaded = true;
        ctrl.templatesService.setIsLoaded(ctrl.isLoaded);
      }

      if (
        $state.current.name !== 'application.templates.edit.exceptions' &&
        $state.current.name !== 'application.templates.create.exceptions'
      ) {
        // Instantiate controller with new $scope
        const TemplatesCreateExceptionsViewModel = $scope.$new(false, $scope),
          TemplatesCreateExceptionsController = $controller('TemplatesCreateExceptionsController', {
            $scope: TemplatesCreateExceptionsViewModel,
          });
      }

      ctrl.getNotificationPreference();
    },

    getNotificationPreference: function () {
      if (ctrl.company && ctrl.company.notificationPreference) {
        ctrl.emailSetting = ctrl.company.notificationPreference;
      }
    },

    initWizard: function () {
      ctrl.wizard = new WizardFactory();
      ctrl.wizard.init([
        {
          name: 'create',
          validators: [
            {
              field: 'templates-edit-header-name',
              len: {
                message: 'Your title must not be empty',
                test: function () {
                  return {
                    value: ctrl.model.name,
                  };
                },
              },
            },
          ],
        },
      ]);
    },

    initColorPicker: function () {
      if (TemplatesService.getColorCategory() && TemplatesService.getColorCategory().backgroundColor) {
        ctrl.spectrumConfig.color = TemplatesService.getColorCategory().backgroundColor;
      }
    },

    clearTabErrors: function () {
      for (const error in ctrl.tabErrors) {
        if (ctrl.tabErrors.hasOwnProperty(error)) {
          ctrl.tabErrors[error] = false;
        }
      }
    },

    setTitleBar: function () {
      $scope.$emit('Application::contentTitleChange', {
        title: HelpersService.cap(ctrl.mode) + ' Event Template',
        buttonLeft: {
          label: 'Close',
          action: () => {
            ctrl.cancel();
          },
        },
        buttonRight: {
          label: ctrl.isMode('edit') ? 'Save' : 'Create & Continue',
          action: () => {
            if (ctrl.isMode('create')) {
              ctrl.saveContinue();
            } else {
              ctrl.save({ publish: false, update: true });
            }
          },
        },
      });
    },

    isMode: function (mode) {
      return ctrl.mode === mode;
    },

    isReviewState: function (state) {
      return TemplatesService.getReviewState() === state;
    },

    isState: function (state: string) {
      if (ctrl.mode === 'create') {
        return $state.current?.name?.indexOf('application.templates.create.' + state) === 0;
      }

      return $state.current?.name?.indexOf('application.templates.edit.' + state) === 0;
    },

    isValid: function (field: string) {
      if (ctrl.isSubmitted) {
        return ValidateService.check(ctrl.validators, field);
      }

      return true;
    },

    hasTabErrors: function (tab: string) {
      return ctrl.tabErrors[tab];
    },

    isTabLoading: function (state: string) {
      return (ctrl.isSaving || !ctrl.isLoaded || ctrl.templatesService.isTabLoading) && ctrl.isState(state);
    },

    getError: function (field: string) {
      return ValidateService.getError(field);
    },

    getState: function (name: string) {
      let stateName = '';
      const stateParams: TODO = {};

      if (!ctrl.templatesService.isTabLoading) {
        stateName = 'application.templates';
        ctrl.templatesService.setIsTabLoading(true, '$$parent');

        if (ctrl.mode === 'create') {
          stateName += '.create.' + name;
        } else {
          stateName += '.edit.' + name;
          stateParams.id = $state.params.id;
        }

        ctrl.save({
          publish: false,
          update: true,
          state: { name: stateName, stateParams: stateParams },
        });
      }
    },

    getCopyTemplate: function (id) {
      ApiService.program
        .get(id, true)
        .then((response) => {
          TemplatesService.setModel(ctrl.processCopyTemplate(response.data.payload));

          ctrl.model = TemplatesService.getModel();
        })
        .catch((response) => {
          ctrl.error.generic(response);
        });
    },

    getCopyEntitlementsTemplate: function (id) {
      ApiService.entitlements
        .detail(id)
        .then((response) => {
          ctrl.entitlementConfig = response.data.payload.entitlementConfig || [];
          ctrl.salesRuleConfig = response.data.payload.salesRuleConfig || [];
          ctrl.ticketRuleConfig = response.data.payload.ticketRuleConfig || [];
        })
        .catch(() => {
          // noop
          // It is OK if there is no entitlement for a template we're copying over.
        });
    },

    getCopyCouponDiscountsTemplate: function (id) {
      ApiService.coupons.templates
        .detail(id)
        .then((response) => {
          ctrl.couponDiscountsConfig = response.data.payload || [];
        })
        .catch(() => {
          // noop
        });
    },

    getCopyAccountDiscountsTemplate: function (id) {
      ApiService.partners.templates
        .detail(id)
        .then((response) => {
          ctrl.accountDiscountsConfig = response.data.payload || [];
        })
        .catch(() => {
          // noop
        });
    },

    getTemplate: function (id) {
      ApiService.program
        .get(id, true)
        .then((response) => {
          TemplatesService.setModel(response.data.payload);
          ctrl.model = TemplatesService.getModel();

          ctrl.setUpdateTimes(ctrl.model);
          ctrl.setTitleBar();
          ctrl.initColorPicker();

          ctrl.isLoaded = true;
          ctrl.templatesService.setIsLoaded(ctrl.isLoaded);

          ValidateService.runFieldTests();

          ctrl.checkPublishingStatus();
        })
        .catch((response) => {
          ctrl.error.generic(response);
          ctrl.isLoaded = true;
          ctrl.templatesService.setIsLoaded(ctrl.isLoaded);
        });
    },

    /**
     * Shows the publishing overlay with the template name
     */
    showPublishingOverlay: function () {
      console.log('[Publishing] showPublishingOverlay called');
      const templateName = ctrl.model.name || 'Event template';
      const message = $sce.trustAsHtml(
        `<strong class="template-name-truncate" title="${templateName}">${templateName}</strong> is currently publishing. This may take a few minutes.`
      );
      console.log('[Publishing] Broadcasting overlay status');
      $rootScope.$broadcast('Application::setOverlayStatus', message, { wide: true });
    },

    /**
     * Hides the publishing overlay
     */
    hidePublishingOverlay: function () {
      $rootScope.$broadcast('Application::setOverlayStatus', null, { wide: false });
    },

    /**
     * Stops any active publishing status polling
     */
    stopPublishingStatusPolling: function () {
      if (ctrl.publishingStatusPollingActor) {
        ctrl.publishingStatusPollingActor.stop();
        ctrl.publishingStatusPollingActor = null;
      }
    },

    /**
     * Starts polling for publishing status updates using the polling machine.
     * Polls until publishing completes (status is no longer IN_PROGRESS).
     */
    startPublishingStatusPolling: function () {
      ctrl.stopPublishingStatusPolling();

      if (!ctrl.model.id) {
        return;
      }

      type PollingResult = { publishingStatus: string };

      const pollingMachine = createPollingMachine<PollingResult>({
        id: 'publishingStatusPolling',
      });

      const actor = createActor(pollingMachine, {
        input: {
          interval: PUBLISHING_STATUS_POLL_INTERVAL_MS,
          maxRetries: 180, // 180 retries × 20 seconds = 1 hour max polling
          promiseFn: () =>
            new Promise<PollingResult>((resolve, reject) => {
              ApiService.program
                .get(ctrl.model.id, true)
                .then((response) => resolve({ publishingStatus: response.data.payload.publishingStatus }))
                .catch(reject);
            }),
          shouldResolve: (response) => response.publishingStatus !== PublishingStatus.IN_PROGRESS,
        },
      });

      actor.subscribe((state) => {
        if (state.status === 'done') {
          $scope.$apply(() => {
            ctrl.model.publishingStatus = state.output.publishingStatus;
            ctrl.hidePublishingOverlay();
            ctrl.publishingStatusPollingActor = null;
          });
        } else if (state.status === 'error') {
          $scope.$apply(() => {
            ctrl.hidePublishingOverlay();
            ctrl.publishingStatusPollingActor = null;
          });
        }
      });

      ctrl.publishingStatusPollingActor = actor;
      actor.start();
    },

    /**
     * Checks current publishing status and updates UI accordingly.
     * Shows overlay and starts polling if IN_PROGRESS, otherwise clears overlay.
     */
    checkPublishingStatus: function () {
      if (ctrl.model.publishingStatus === PublishingStatus.IN_PROGRESS) {
        ctrl.showPublishingOverlay();
        ctrl.startPublishingStatusPolling();
      } else {
        ctrl.hidePublishingOverlay();
        ctrl.stopPublishingStatusPolling();
      }
    },

    getDeletedEvents: function () {
      const props = {
        templateId: ctrl.model.id,
        startTime: CalendarService.toISO8601(new Date().setHours(0, 0, 0, 0)),
      };

      ApiService.program.trash
        .list(props, HelpersService.getPaginator())
        .then((response) => {
          ctrl.hasDeletedEvents = response.data.payload.list && response.data.payload.list.length > 0;
          $scope.$emit('TemplatesCreateController::getDeletedEvents', ctrl.hasDeletedEvents);
        })
        .catch((response) => {
          ctrl.error.generic(response);
        });
    },

    remove: function () {
      $uibModal
        .open<boolean>({
          templateUrl: 'views/application/views/programs/modals/removeEvent.tpl.html',
          size: 'md',
          controller: 'ProgramsRemoveModalController',
          resolve: {
            currentEvent: () => ctrl.model,
            SoldQuantity: () => ctrl.model.soldQuantity,
            emailSetting: () => ctrl.emailSetting,
          },
        })
        .result.then((sendEmail) => {
          ApiService.program
            .remove({ id: ctrl.model.id, sendEmail: sendEmail })
            .then((response) => {
              ValidateService.clearFieldTests();
              $state.go('application.programs.templatesList');
            })
            .catch((response) => {
              ctrl.error.generic(response);
            });
        })
        .catch(() => {
          // noop
        });
    },

    removePublishFieldTests: function () {
      ValidateService.removeFieldTest({
        field: 'templates-edit-header-price-list',
        equal: {
          message: 'You must select a price list for the event',
          test: function () {
            const invalidSave = !TemplatesService.hasPriceLists();
            return [invalidSave, false];
          },
        },
      });

      ValidateService.removeFieldTest({
        field: 'templates-edit-header-schedule',
        equal: {
          message: 'You must create a schedule for the event',
          test: function () {
            const invalidSave = !TemplatesService.hasSchedules();
            return [invalidSave, false];
          },
        },
      });
    },

    addPublishFieldTests: function () {
      ValidateService.addFieldTests([
        {
          field: 'templates-edit-header-price-list',
          equal: {
            message: 'You must select a price list for the event',
            test: function () {
              const invalidSave = !TemplatesService.hasPriceLists();
              return [invalidSave, false];
            },
          },
        },
        {
          field: 'templates-edit-header-schedule',
          equal: {
            message: 'You must create a schedule for the event',
            test: function () {
              const invalidSave = !TemplatesService.hasSchedules();
              return [invalidSave, false];
            },
          },
        },
      ]);
    },

    hasValidBasePrices: function () {
      const priceList = TemplatesService.getPriceList();
      const prices = ensureArray(priceList?.prices);

      return prices.every((item) => {
        return isValidBasePrice({ basePrice: item.basePrice ?? null, price: item.price });
      });
    },

    cancel: function () {
      ValidateService.clearFieldTests();
      $state.go('application.programs.templatesList');
    },

    save: function (props: { publish: boolean; update: boolean; state?: { name: string; stateParams: StateParams } }) {
      if (ctrl.isSaving) {
        return;
      }

      // Check if a publish is already in progress and user is trying to publish
      if (props.publish && ctrl.model.publishingStatus === PublishingStatus.IN_PROGRESS) {
        ctrl.showPublishingInProgressModal();
        return;
      }

      ctrl.performSave(props);
    },

    performSave: function (props: { publish: boolean; update: boolean; state?: { name: string; stateParams: StateParams } }) {
      ctrl.isUpdate = props.update;
      ctrl.isSaving = true;
      ctrl.isSubmitted = true;
      // Trigger save event downstream to subviews
      $scope.$broadcast('TemplatesCreateController::Save');

      // next tick to give time for save to propagate
      $timeout(() => {
        ctrl.removePublishFieldTests();

        if (props.publish) {
          ctrl.addPublishFieldTests();
          ValidateService.runFieldTests();
        } else {
          ValidateService.runFieldTests();
        }
      }, 100).then(() => {
        // Any failed validators will be passed to the service
        if (ValidateService.errors.length === 0 && ctrl.hasValidBasePrices()) {
          if (props.publish) {
            TemplatesService.setReviewState('published');
            ctrl.isPublishing = true;
            ctrl.model.publishingStatus = PublishingStatus.IN_PROGRESS;
            ctrl.showPublishingOverlay();
            // Don't start polling yet - wait for API call to complete
          } else {
            ctrl.isPublishing = false;
          }

          // APP-5100: Diagnostic — detect when an existing template is about to be saved with no
          // member discount rules. This can indicate the race condition where the Members controller
          // skipped writing entitlementConfiguration to the service model during the save broadcast.
          // Only check on edit mode (templates with an id) since new templates have no entitlements yet.
          const modelToSave = TemplatesService.getModel();
          const entitlement = modelToSave.entitlementConfiguration;
          if (modelToSave.id && (!entitlement || !entitlement.entitlementConfig || entitlement.entitlementConfig.length === 0)) {
            const emptyEntitlementMetadata = {
              templateId: modelToSave.id,
              mode: ctrl.mode,
              hasEntitlement: !!entitlement,
              entitlementConfigLength: entitlement?.entitlementConfig?.length ?? 0,
              isTabLoading: ctrl.templatesService.isTabLoading,
              tabName: ctrl.templatesService.tabName,
              isSaving: ctrl.isSaving,
            };
            console.warn('[APP-5100] Saving template with empty entitlementConfiguration:', emptyEntitlementMetadata);
            bugsnagService.notify(
              new Error('[APP-5100] Template saved with empty entitlementConfiguration'),
              (event: TODO) => {
                event.context = 'templates.save.empty_entitlement';
                event.severity = 'warning';
                event.addMetadata('templateState', emptyEntitlementMetadata);
              }
            );
          }

          if (ctrl.mode === 'create') {
            ctrl.create().then(() => {
              ctrl.templatesService.setIsTabLoading(false, '$$parent');
              // Start polling after create completes (server has started publishing)
              if (props.publish) {
                ctrl.startPublishingStatusPolling();
              }
            });
          } else if (ctrl.mode === 'edit') {
            ctrl.update(props.state).then(() => {
              ctrl.templatesService.setIsTabLoading(false, '$$parent');
              // Start polling after update completes (server has started publishing)
              if (props.publish) {
                ctrl.startPublishingStatusPolling();
              }
            });
          }
        } else {
          ctrl.isSaving = false;
          ctrl.templatesService.setIsTabLoading(false, '$$parent');
        }

        if (props.state && props.state.name !== $state.current.name) {
          // APP-5100: Diagnostic — detect when we navigate away while a save API call is still
          // in flight. isSaving remains true until update()/create() resolves, so if it's still
          // true here, the $state.go() fires before the API call completes.
          if (ctrl.isSaving) {
            const navDuringSaveMetadata = {
              fromState: $state.current.name,
              toState: props.state.name,
              isTabLoading: ctrl.templatesService.isTabLoading,
              tabName: ctrl.templatesService.tabName,
              templateId: ctrl.model?.id,
              mode: ctrl.mode,
            };
            console.warn('[APP-5100] Navigating to new tab while save is still in flight:', navDuringSaveMetadata);
            bugsnagService.notify(
              new Error('[APP-5100] Tab navigation fired before save API call completed'),
              (event: TODO) => {
                event.context = 'templates.save.navigating_during_save';
                event.severity = 'warning';
                event.addMetadata('navigationState', navDuringSaveMetadata);
              }
            );
          }
          $state.go(props.state.name, props.state.stateParams);
        } else {
          ctrl.templatesService.setIsTabLoading(false, '$$parent');
        }
      });
    },

    saveContinue: function () {
      if (ctrl.isSaving) {
        return;
      }

      ctrl.isSubmitted = true;
      ctrl.isSaving = true;
      // Trigger save event downstream to subviews
      $scope.$broadcast('TemplatesCreateController::Save');

      ValidateService.runFieldTests();

      $timeout(() => {
        // Any failed validators will be passed to the service
        if (ValidateService.errors.length === 0 && ctrl.hasValidBasePrices()) {
          if (ctrl.mode === 'create') {
            ctrl.create();
          }
        } else {
          ctrl.isSaving = false;
          ctrl.templatesService.setIsTabLoading(false, '$$parent');
        }
      }, 0);
    },

    showTemplates: function () {
      $uibModal
        .open<TODO>({
          templateUrl: 'views/application/views/templates/modals/showTemplates.tpl.html',
          size: 'lg',
          controller: 'TemplatesShowModalController',
          resolve: {
            selectedTemplate: () => false,
          },
        })
        .result.then((template) => {
          ctrl.getCopyTemplate(template.id);
          ctrl.getCopyEntitlementsTemplate(template.id);
          ctrl.getCopyCouponDiscountsTemplate(template.id);
          ctrl.getCopyAccountDiscountsTemplate(template.id);
        })
        .catch(() => {
          // noop
        });
    },

    showDeletedEventsMessage: function () {
      $uibModal
        .open({
          templateUrl: 'views/application/views/templates/modals/showDeletedEventsModal.tpl.html',
          size: 'md',
          controller: 'TemplatesShowDeletedEventsModalController',
        })
        .result.then(() => {
          const startTime = CalendarService.toISO8601(new Date().setHours(0, 0, 0, 0));

          ValidateService.clearFieldTests();
          $state.go('application.programs.list', {
            templateId: ctrl.model.id,
            templateName: ctrl.model.name,
            startTime: startTime,
          });
        })
        .catch(() => {
          if (!ctrl.isUpdate) {
            ValidateService.clearFieldTests();
            $state.go('application.programs.templatesList');
          }
        });
    },

    showPublishModal: function (template) {
      if (ctrl.isPublishing) {
        const doneFn = (ignoreTemplateLock: TODO) => {
          if (!ignoreTemplateLock && FeatureService.isEnabled(FeatureService.objectLock)) {
            ctrl.cancel();
          }
        };

        $uibModal
          .open({
            templateUrl: 'views/application/views/templates/modals/published/published.tpl.html',
            size: 'md',
            controller: 'TemplatesModalsPublishedController',
            resolve: {
              template: () => template,
            },
          })
          .result.then(doneFn)
          .catch(doneFn);
      }
    },

    showPublishingInProgressModal: function () {
      $uibModal
        .open<PublishingInProgressModalResult>({
          templateUrl: 'views/application/views/templates/modals/publishingInProgress/publishingInProgress.tpl.html',
          size: 'md',
          controller: 'TemplatesModalsPublishingInProgressController',
          controllerAs: '$ctrl',
          resolve: {
            template: () => ctrl.model,
          },
        })
        .result.then((result) => {
          if (result && result.action === 'startNew') {
            // User chose to start a new publish, proceed with save
            ctrl.performSave({ publish: true, update: true });
          }
          // If action is 'letFinish' or modal was dismissed, do nothing
        })
        .catch(() => {
          // Modal was dismissed, do nothing
        });
    },

    create: function () {
      return ApiService.program
        .create(TemplatesService.getModel(true), ctrl.isPublishing)
        .then((response) => {
          const templateId = response.data.payload.id;

          ctrl.showPublishModal(response.data.payload);
          ctrl.isSubmitted = false;
          ctrl.isSaving = false;
          ctrl.isPublishing = false;

          ctrl.setUpdateTimes(response.data.payload);

          return $q
            .all([
              ctrl.createEntitlements(response.data.payload.id),
              ctrl.createCouponDiscounts(response.data.payload.id),
              ctrl.createAccountDiscounts(response.data.payload.id),
            ])
            .then(() => {
              if (!ctrl.isUpdate) {
                $state.go('application.templates.edit.description', { id: templateId });
              }
            })
            .catch(() => {
              // noop
            });
        })
        .catch((response) => {
          ctrl.isSubmitted = false;
          ctrl.isSaving = false;
          ctrl.isPublishing = false;
          ctrl.error.generic(response);
        });
    },

    createEntitlements: function (templateId) {
      const props: TODO = {};

      if (
        (ctrl.entitlementConfig && ctrl.entitlementConfig.length > 0) ||
        (ctrl.salesRuleConfig && ctrl.salesRuleConfig.length > 0) ||
        (ctrl.ticketRuleConfig && ctrl.ticketRuleConfig.length > 0)
      ) {
        props.entitlementConfig = ctrl.entitlementConfig;
        props.salesRuleConfig = ctrl.salesRuleConfig;
        props.ticketRuleConfig = ctrl.ticketRuleConfig;
        ApiService.entitlements
          .create(templateId, props)
          .then(() => {
            // noop
          })
          .catch((response) => {
            ctrl.error.generic(response);
          });
      }
    },

    createCouponDiscounts: function (templateId) {
      const props: TODO[] = [];

      if (ctrl.couponDiscountsConfig && ctrl.couponDiscountsConfig.length) {
        for (let i = 0; i < ctrl.couponDiscountsConfig.length; i++) {
          props.push({
            id: null,
            eventTemplateId: templateId,
            categoryId: ctrl.couponDiscountsConfig[i].categoryId,
            discountId: ctrl.couponDiscountsConfig[i].discountId,
          });
        }

        ApiService.coupons.templates
          .update({ eventTemplateId: templateId, data: props })
          .then(() => {
            // noop
          })
          .catch((response) => {
            ctrl.error.generic(response);
          });
      }
    },

    createAccountDiscounts: function (templateId) {
      const props: TODO[] = [];

      if (ctrl.accountDiscountsConfig && ctrl.accountDiscountsConfig.length) {
        for (let i = 0; i < ctrl.accountDiscountsConfig.length; i++) {
          props.push({
            id: null,
            eventTemplateId: templateId,
            categoryId: ctrl.accountDiscountsConfig[i].categoryId,
            discountId: ctrl.accountDiscountsConfig[i].discountId,
          });
        }

        ApiService.partners.templates
          .update({ eventTemplateId: templateId, data: props })
          .then(() => {
            // noop
          })
          .catch((response) => {
            ctrl.error.generic(response);
          });
      }
    },

    update: function (state: TODO) {
      return ApiService.program
        .update(TemplatesService.getModel(true), ctrl.isPublishing)
        .then((response) => {
          if (state) {
            // TODO: APP-4332 - product - why?
            // If there is a state don't check for deleted events
            ctrl.isSubmitted = false;
            ctrl.isSaving = false;
            ctrl.isPublishing = false;
          } else {
            ctrl.showPublishModal(response.data.payload);
            ctrl.isSubmitted = false;
            ctrl.isSaving = false;
            ctrl.isPublishing = false;

            ctrl.setUpdateTimes(response.data.payload);
            ctrl.getDeletedEvents();
          }
        })
        .catch((response) => {
          ctrl.isSubmitted = false;
          ctrl.isSaving = false;
          ctrl.isPublishing = false;
          ctrl.error.generic(response);
        });
    },

    processCopyTemplate: function (template) {
      // Remove old event ID to prevent overwriting
      delete template.id;

      template.name += ' (COPY)';
      template.reviewState = 'draft';
      if (template.colorCategory && template.colorCategory.backgroundColor) {
        ctrl.spectrumConfig.color = template.colorCategory.backgroundColor;
      } else {
        template.colorCategory = angular.copy(TemplatesService.getColorCategory());
        ctrl.spectrumConfig.color = TemplatesService.getColorCategory().backgroundColor;
      }

      return template;
    },

    setUpdateTimes: function (template) {
      if (template) {
        if (template.lastUpdated) {
          ctrl.lastUpdated =
            CalendarService.toFormat(CalendarService.parseDate(template.lastUpdated), ctrl.dateFormat) +
            ' at ' +
            CalendarService.toFormat(CalendarService.parseDate(template.lastUpdated), ctrl.hourFormat);
        }
        if (template.lastPublished) {
          ctrl.lastPublished =
            CalendarService.toFormat(CalendarService.parseDate(template.lastPublished), ctrl.dateFormat) +
            ' at ' +
            CalendarService.toFormat(CalendarService.parseDate(template.lastPublished), ctrl.hourFormat);
        }
      }
    },

    error: {
      generic: function (response: { data: WrappedACMEErrorResponse }) {
        ctrl.onError(response, { message: response.data.error });
      },
    },

    onDeletedEventsUpdate: function (n, o) {
      if (ctrl.hasDeletedEvents && PermissionsService.check('EVENT_RESTORE')) {
        ctrl.showDeletedEventsMessage();
      } else {
        if (!ctrl.isUpdate) {
          ValidateService.clearFieldTests();
          $state.go('application.programs.templatesList');
        }
      }
    },

    onError: function (e, data: { message: string; callback?: unknown; state?: unknown; stateParams?: unknown }) {
      $uibModal
        .open({
          templateUrl: 'views/application/views/reports/create/modals/showMessage.tpl.html',
          size: 'md',
          controller: 'ReportsCreateShowMessageModalController',
          resolve: {
            currentTitle: () => 'An error has occurred',
            currentMessage: () => data.message || 'An unknown error has occurred.',
            safe: () => true,
          },
        })
        .result.then(() => {
          if (typeof data.callback === 'function') {
            data.callback();
          }

          if (data.state) {
            if (data.stateParams) {
              $state.go(data.state, data.stateParams);
            } else {
              $state.go(data.state);
            }
          }
        })
        .catch(() => {
          // noop
        });
    },

    onTabCheck: function (e, data) {
      ctrl.tabErrors[data.tab] = data.invalid;
    },

    onDateFormatLoad: function (e, data) {
      ctrl.dateFormat = UserPreferencesService.getDateFormat('shortDate', 'dateFormat');
      ctrl.hourFormat = UserPreferencesService.getHourFormat('standardTime', 'hourFormat');
    },

    onColorCodesUpdate: function (n, o) {
      if (n && n !== o) {
        TemplatesService.setColorCategory(n);
      }
    },
  };

  $scope.templatesCreate = ctrl;
  ctrl.init();
}

export const TemplatesCreateControllerModule = angular
  .module('acme.view.application.templates.create.controllers', [
    'ui.bootstrap',
    'acme.common.service.api',
    'acme.common.service.helpers',
    'acme.common.service.templates',
    BugsnagServiceModule,
    'acme.common.service.permissions',
    'acme.common.service.validate',
    'acme.common.service.themesHelper',
    'acme.common.factory.wizard',
    'acme.common.service.userPreferences',
    FeatureServiceModule,
    'acme.view.application.templates.modals.published.controllers',
    TemplatesModalsPublishingInProgressControllerModule,
    TemplatesCreateExceptionsControllerModule,
    'acme.view.application.templates.showTemplatesModal.controllers',
    'acme.view.application.templates.showDeletedEventsModal.controllers',
  ])
  .controller('TemplatesCreateController', TemplatesCreateController).name;
